import cx from "classnames";
import React from "react";
import { LoadingIndicator, Photo, TextEllipsis } from "component-library";
import { i18n } from "i18n";
import { prepareBreakdownDataTitle } from "shared/helpers/breakdownViewHelpers";
import { IBreakdownDetails, LinkmailType } from "shared/models/breakdown";

import { IFacetGroup } from "shared/models/facet";

import { ISuggestion } from "models/suggestion";
import dateService, { dateFormats } from "services/dateService";

import { getImageUrl, ImageLocation } from "services/urlBuilder";
import "./Card.scss";
import { dataTestIds } from "data-testids";

export interface ICardTexts {
  remove: string;
  more: string;
  less: string;
  inResponse: string;
  amendment: string;
}

export interface ICardProps {
  suggestion: ISuggestion;
  texts: ICardTexts;
  breakdown: IBreakdownDetails;
  productionTypes: IFacetGroup[];
  onDelete?: (ids: string[]) => Promise<boolean>;
  isPrintView?: boolean;
}

export interface ICardState {
  isShowFullComment: boolean;
  isLoading?: boolean;
  isRemoved?: boolean;
}

interface IPerformerCardCommentsProps {
  comments: string;
  texts: {
    more: string;
    less: string;
  };
  switchCommentView: (flag: boolean) => (event: React.MouseEvent) => void;
  isShowFullComment: boolean;
}

interface IPerformerCardNoteProps {
  comments: string;
  viewNotesLabel: string;
  handleMoreClick: (event: React.MouseEvent) => void;
}

export const PerformerCardNote: React.FC<
  React.PropsWithChildren<IPerformerCardNoteProps>
> = ({ comments, viewNotesLabel, handleMoreClick }) => {
  return (
    <TextEllipsis
      text={comments}
      basedOn="words"
      ellipsis={
        <span
          className="c-performer-card__comment-more"
          onClick={handleMoreClick}
        >
          {`...${viewNotesLabel}`}
        </span>
      }
      maxLine={2}
    />
  );
};

export const PerformerComments: React.FC<
  React.PropsWithChildren<IPerformerCardCommentsProps>
> = ({ comments, texts, switchCommentView, isShowFullComment }) => {
  return isShowFullComment ? (
    <div className="c-performer-card__comment-full">
      {comments}
      <span
        className="c-performer-card__comment-less"
        id="modal-close-button"
        onClick={switchCommentView(false)}
        tabIndex={0}
      >
        {` ${texts.less}`}
      </span>
    </div>
  ) : (
    <div className="c-performer-card__comment-preview">
      <PerformerCardNote
        comments={comments}
        viewNotesLabel={texts.more}
        handleMoreClick={switchCommentView(true)}
      />
    </div>
  );
};

export class Card extends React.Component<ICardProps, ICardState> {
  private cardContainerRef = React.createRef<HTMLDivElement>();
  private commentsAreaRef = React.createRef<HTMLDivElement>();
  private _isMounted = false;

  public readonly state: Readonly<ICardState> = {
    isShowFullComment: false,
  };

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  public render() {
    const { t } = i18n;
    const {
      suggestion: {
        id,
        dateCreated,
        performer,
        photo,
        _links: { profile },
        comment,
        suggestedBy,
      },
      texts,
      onDelete,
      isPrintView,
    } = this.props;

    const suggestedDate = dateService.format(dateCreated, dateFormats.date);
    const suggestedTime = dateService.format(
      dateCreated,
      dateFormats.timeShort
    );
    const fullName = `${performer.forenames} ${performer.surname}`;
    const cardClassNames = cx({
      "c-performer-card g-inner-grid g-col-lg-12": true,
      "c-performer-card--removed": this.state.isRemoved,
      "c-performer-card--print": isPrintView,
    });

    return (
      <div
        ref={this.cardContainerRef}
        className={cardClassNames}
        id={id}
        data-testid={
          dataTestIds.nuJobsApp["SuggestionViewAgent.Card.SuggestedPerformer"]
        }
      >
        <div className="g-col-lg-3 g-col-md-4 g-sm-12 c-performer-card__main-info">
          <a
            href={profile.href}
            target="_blank"
            className="c-performer-card__image-container"
            tabIndex={0}
            rel="noreferrer"
          >
            <Photo
              className="c-performer-card__image"
              src={getImageUrl(photo.fileName, ImageLocation.MainPhoto)}
              alt={fullName}
              hasLoadingSpinner={true}
            />
          </a>
          <a
            href={profile.href}
            target="_blank"
            className="c-performer-card__name"
            tabIndex={0}
            rel="noreferrer"
          >
            {fullName}
          </a>
        </div>

        <div className="g-col-lg-6 g-col-md-6 g-sm-12 c-performer-card__content">
          {comment && (
            <div
              className="c-performer-card__comment"
              ref={this.commentsAreaRef}
            >
              <PerformerComments
                comments={comment}
                texts={{ more: texts.more, less: texts.less }}
                switchCommentView={this.switchCommentView}
                isShowFullComment={this.state.isShowFullComment}
              />
            </div>
          )}
          <div className="c-performer-card__suggested-info">
            <span className={"c-performer-card__ellipsis"}>
              {`${suggestedDate} ${suggestedTime} | ${
                texts.inResponse
              } ${this.getBreakdownDataTitle()}`}{" "}
              {t("common:suggestions.suggestedBy", { name: suggestedBy })}
            </span>
          </div>
        </div>
        {onDelete && this.renderDeleteButton()}
      </div>
    );
  }

  private renderDeleteButton = () => {
    const { texts } = this.props;
    const { isLoading } = this.state;

    return (
      <div className="g-col-lg-3 g-col-md-2 g-sm-12 c-performer-card__button">
        {isLoading ? (
          <div className="c-performer-card__loader">
            <LoadingIndicator variant="tertiary" />
          </div>
        ) : (
          <a
            tabIndex={0}
            className="g-alternative-link g-alternative-link--lg"
            onClick={this.onDelete}
          >
            {texts.remove}
          </a>
        )}
      </div>
    );
  };

  private switchCommentView = (isShowFullComment: boolean) => () => {
    this.setState({
      isShowFullComment,
    });
  };

  private getBreakdownDataTitle = () => {
    const {
      breakdown,
      suggestion,
      productionTypes,
      texts: { amendment },
    } = this.props;

    return prepareBreakdownDataTitle(
      {
        title: breakdown.production.title,
        productionTypeId: breakdown.production.typeId,
        linkmailType:
          suggestion.amendmentCount === 0
            ? LinkmailType.Default
            : LinkmailType.Amendment,
        amendmentCount: suggestion.amendmentCount,
      },
      { productionTypes, linkmailTypeAmendment: amendment + "{number}" }
    );
  };

  private onDelete = async () => {
    this.setState({ isLoading: true });
    const isRemoved = await this.props.onDelete?.([this.props.suggestion.id]);

    if (this._isMounted) {
      this.setState({ isLoading: false, isRemoved });
    }
  };
}
